import { DependencyList, RefObject, useEffect, useState } from 'react';
import { filter, map, ReplaySubject } from 'rxjs';
import { AgGridReact } from 'ag-grid-react';

export interface SelectedRow<TPayload = any> {
  id: UUID;
  payload: TPayload;
}

export enum SelectionEventInitiator {
  GRID,
  EXTERNAL_INITIATOR
}

export interface SelectionEvent<TPayload = any> {
  initiator: SelectionEventInitiator;
  selectedGridRows: SelectedRow<TPayload>[];
}

interface UseSelectionSubscriptionParams {
  gridInstance: RefObject<AgGridReact<any>>;
  observable: ReplaySubject<SelectionEvent>;
  dependenciesToInvalidateSelection: DependencyList;
}

export function useGridSelectionSubscription({
  gridInstance,
  observable,
  dependenciesToInvalidateSelection
}: UseSelectionSubscriptionParams) {
  useEffect(() => {
    const subscription = observable
      .pipe(
        filter((el) => el.initiator === SelectionEventInitiator.EXTERNAL_INITIATOR),
        map((el) => el.selectedGridRows)
      )
      .subscribe({
        next: (data) => {
          if (!gridInstance?.current?.api) return;
          gridInstance?.current?.api.deselectAll();
          gridInstance?.current?.api.forEachNode((node) => {
            if (data.find((el) => el.id === node.id)) {
              node.setSelected(true);
            }
          });
        }
      });
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dependenciesToInvalidateSelection.length > 0) {
      observable.next({
        initiator: SelectionEventInitiator.GRID,
        selectedGridRows: []
      });
    }
  }, [dependenciesToInvalidateSelection, observable]);
}

export function useGridSelectedRows<TPayload = any>(
  observable: ReplaySubject<SelectionEvent<TPayload>>,
) {
  const [selectedRows, setSelectedRows] =
    useState<SelectedRow<TPayload>[]>([]);

  useEffect(() => {
    const subscription = observable
      .pipe(map((event) => event.selectedGridRows))
      .subscribe({
        next: (data) => setSelectedRows(data)
      });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return selectedRows;
}

export interface RefreshEvent {
  initiator: string;
}

interface UseDataRefreshParams {
  gridInstance: RefObject<AgGridReact<any>>;
  observable: ReplaySubject<RefreshEvent>;
}

export function useGridDataRefresh({
  gridInstance,
  observable
}: UseDataRefreshParams) {
  useEffect(() => {
    const subscription = observable.subscribe({
      next: () => {
        if (!gridInstance?.current?.api) return;
        gridInstance.current.api.refreshCells({ force: true });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [gridInstance, observable]);
}